import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Alert, Button } from "reactstrap";

import { userHasAnyRole } from "../../../services/authService";
import { buildDayClosePath } from "../../../services/dayCloseReportService";
import { getSettings } from "../../../services/tenantSettingService";

const DISMISS_PREFIX = "adatis.dayCloseReminder.dismissed.";

const todayIsoDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Reminder near end of day for owner/manager to review day close.
 * Hour comes from settings (or unit schedule when automatic).
 */
const DayCloseReminderBanner = () => {
  const { t } = useTranslation();
  const today = todayIsoDate();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(`${DISMISS_PREFIX}${today}`) === "1";
    } catch {
      return false;
    }
  });
  const [alreadyReviewed, setAlreadyReviewed] = useState(() => {
    try {
      return localStorage.getItem(`adatis.dayClose.reviewed.${today}`) === "1";
    } catch {
      return false;
    }
  });
  const [reminderHour, setReminderHour] = useState(18);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!userHasAnyRole(["owner", "manager"])) {
        setReady(true);
        return;
      }

      try {
        const [settingsResponse, reportModule] = await Promise.all([
          getSettings(),
          import("../../../services/dayCloseReportService"),
        ]);

        if (cancelled) {
          return;
        }

        setReminderHour(
          settingsResponse.data.day_close_reminder_hour_resolved ?? 18
        );

        try {
          const report = await reportModule.getDayCloseReport({
            date: today,
            consolidate: true,
          });
          if (!cancelled) {
            setAlreadyReviewed(Boolean(report.data.day_fully_reviewed || report.data.reviewed));
          }
        } catch {
          // Single-unit tenants may fail consolidate without units — ignore
        }
      } catch {
        // Keep default hour
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [today]);

  const shouldShow = useMemo(() => {
    if (!ready || !userHasAnyRole(["owner", "manager"])) {
      return false;
    }

    if (dismissed || alreadyReviewed) {
      return false;
    }

    return new Date().getHours() >= reminderHour;
  }, [ready, dismissed, alreadyReviewed, reminderHour]);

  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(`${DISMISS_PREFIX}${today}`, "1");
    } catch {
      // ignore
    }
    setDismissed(true);
  }, [today]);

  if (!shouldShow) {
    return null;
  }

  return (
    <Alert
      color="info"
      className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4"
    >
      <div className="d-flex align-items-start gap-2">
        <i className="ri-safe-2-line fs-4 mt-1" aria-hidden />
        <div>
          <strong className="d-block">
            {t("studio.dashboard.dayCloseReminder.title")}
          </strong>
          <span className="text-body">
            {t("studio.dashboard.dayCloseReminder.description")}
          </span>
        </div>
      </div>
      <div className="d-flex flex-wrap gap-2">
        <Button
          tag={Link}
          to={buildDayClosePath({ date: today })}
          color="info"
          size="sm"
          className="flex-shrink-0"
        >
          {t("studio.dashboard.dayCloseReminder.cta")}
        </Button>
        <Button
          color="soft-secondary"
          size="sm"
          className="flex-shrink-0"
          onClick={handleDismiss}
        >
          {t("studio.dashboard.dayCloseReminder.dismiss")}
        </Button>
      </div>
    </Alert>
  );
};

export default DayCloseReminderBanner;
